import React, { useMemo } from 'react'

import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import type { Components } from 'react-markdown'

import { glossaryTerm } from 'docs/glossary'
import { useGlossaryCorpus } from 'docs/GlossaryProvider'

import { Term, termInlineSx } from '../Term'

interface Props {
  /** A glossary entry's `long`. Rendered verbatim — never paraphrased (§44). */
  text: string
  /**
   * Given, a reference calls this instead of opening a `TermOverlay`.
   *
   * The one call site that needs it is `TermOverlay` itself, which cannot open another
   * copy of the thing it already is (§52). It swaps its own body and pushes onto its
   * trail, which is exactly what its Related chips do — so both routes to a related term
   * behave identically rather than one navigating and one nesting.
   */
  onTermClick?: (id: string) => void
  sx?: React.ComponentProps<typeof Typography>['sx']
}

/**
 * A glossary definition's `long`, rendered as prose with its inline term references live.
 *
 * ## Why this exists
 *
 * `long` used to render as `<Typography>{entry.long}</Typography>` in both places the
 * platform shows it — the glossary page and `TermOverlay`. That was correct for as long
 * as `long` was plain text, and became a latent bug the moment the corpus was allowed to
 * carry `[the words](term:other-id)`: a plain `Typography` prints markdown as its own
 * SOURCE, so every authored reference would have shipped to the glossary page as visible
 * bracket-and-parenthesis noise. The consumer app's `TermSheet` had already been taught
 * to render it; these two had not, and nothing compares them.
 *
 * ## The audience floor makes this safe with no filtering
 *
 * A reference is guaranteed resolvable in whatever corpus the reader has. The publisher
 * enforces that an entry may only reference terms at or BELOW its own floor, so if the
 * reader can see this entry (floor ≤ their rank) they can see everything it names
 * (floor ≤ this entry's floor ≤ their rank). That closure is the reason a reference can
 * sit mid-sentence at all, where a `seeAlso` chip — which may point upward and is simply
 * dropped when it does — cannot.
 *
 * The unresolved branch below is therefore unreachable rather than load-bearing. It is
 * kept because the alternative failure is a word that looks tappable and does nothing,
 * and because a corpus is data that a future edit could get wrong before the guard runs.
 */
export const TermProse = ({ text, onTermClick, sx }: Props) => {
  const theme = useTheme()
  const corpus = useGlossaryCorpus()

  const components = useMemo<Components>(
    () => ({
      a: ({ href, children }) => {
        const id = href?.startsWith('term:') ? href.slice('term:'.length) : undefined

        // Not a term. Render the words and DROP the link: a definition has no business
        // navigating the reader out of the surface they opened it on, and a dead anchor
        // that looks tappable is worse than plain prose.
        if (!id) return <>{children}</>

        if (!onTermClick) return <Term id={id}>{children}</Term>

        if (!glossaryTerm(id, corpus)) return <>{children}</>

        return (
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={() => onTermClick(id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onTermClick(id)
              }
            }}
            sx={termInlineSx(theme, 'pointer')}
          >
            {children}
          </Box>
        )
      },
    }),
    [corpus, onTermClick, theme],
  )

  return (
    <Typography component="div" sx={{ '& p': { margin: 0 }, '& p + p': { marginTop: 3 }, ...sx }}>
      <ReactMarkdown
        /*
          Without this the `term:` scheme never reaches the `a` override at all.
          `defaultUrlTransform` allows http|https|ircs|mailto|xmpp and rewrites every
          other scheme to the EMPTY STRING, so the href would arrive blank, the test
          above would never fire, and the reference would render as ordinary dead text
          with no error anywhere. §44 flags this exact trap; `DocContent` and the
          consumer app's `TermSheet` carry the same widening for the same reason.
        */
        urlTransform={url => (url.startsWith('term:') ? url : defaultUrlTransform(url))}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </Typography>
  )
}
